import { Grid2 } from '@mui/material'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import React from 'react'

import CampaignLink from '@/app/component/atom/campaign-link'
import Tooltip from '@/app/component/atom/tooltip'
import { dateTime } from '@/app/service/util'
import { currencyFormat, financialNumber } from '@/app/service/util/format'

import { DataCell, RowContainer, RowLoader } from './common'
import ContactIcon from './contact-icon'
import ContactPhoneDisplay from './contact-phone-display'

type Props = {
  campaignId?: number
  campaignName?: string
  contactDeleted: boolean
  contactId: number
  created: string
  done: boolean
  error: string
  isLoadingRow: boolean
  phoneNumber: number
  priceCharged: string
  success: boolean
  topupCurrency: string
  topupReceived: number
  topupRequested: number

  viewContact: (contactId: number) => void
}

const processStatus = (done: boolean, success: boolean, error: string): string => {
  if (!done) {
    return 'Pending'
  }
  if (success) {
    return 'Success'
  }
  if (error) {
    return `Failed: ${error}`
  }
  return 'Unknown'
}

const processTopupValue = (topupReceived: number, topupCurrency: string, topupRequested: number): string => {
  if (topupReceived) {
    return `(${topupCurrency} ${financialNumber(topupReceived)})`
  }
  if (topupRequested) {
    if (topupCurrency) {
      return `(${topupCurrency} ${financialNumber(topupRequested)} requested)`
    }
    return `(${financialNumber(topupRequested)} requested)`
  }
  return '-'
}

const TopupRow: React.FC<Props> = (props) => {
  const {
    campaignId,
    campaignName,
    contactDeleted,
    contactId,
    created,
    done,
    error,
    isLoadingRow,
    phoneNumber,
    priceCharged,
    success,
    topupCurrency,
    topupReceived,
    topupRequested,
    viewContact,
  } = props

  const messageStatus = processStatus(done, success, error)
  const topupValue = processTopupValue(topupReceived, topupCurrency, topupRequested)

  return (
    <RowContainer>
      {isLoadingRow ? (
        <CardContent>
          <Grid2 container spacing={8}>
            <RowLoader size={{ xs: 12 }}>
              <CircularProgress color="primary" size={50} />
            </RowLoader>
          </Grid2>
        </CardContent>
      ) : (
        <CardContent>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 3, md: 3 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ContactPhoneDisplay phoneNumber={phoneNumber} />
            </Grid2>

            <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <span>{messageStatus}</span>
              </Typography>
              <Typography color="primary" align="center" variant="body1">
                <b>({currencyFormat(priceCharged)})</b>
              </Typography>
            </DataCell>

            <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <span>Top-up amount</span>
              </Typography>
              <Typography color="primary" align="center" variant="body1">
                <b>{topupValue}</b>
              </Typography>
            </DataCell>

            <DataCell
              size={{ xs: 2, sm: 2, md: 2 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="textSecondary" align="center">
                <Tooltip title={'Date and Time'}>
                  <span>&nbsp;{dateTime(created)}</span>
                </Tooltip>
              </Typography>
            </DataCell>
            <DataCell size={{ xs: 2, sm: 2, md: 2 }}>
              <CampaignLink align="center" campaignId={campaignId} campaignName={campaignName} />
            </DataCell>
            <Grid2
              size={{ xs: 2, sm: 1, md: 1 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ContactIcon contactDeleted={contactDeleted} contactId={contactId} onClick={viewContact} />
            </Grid2>
          </Grid2>
        </CardContent>
      )}
    </RowContainer>
  )
}

export default TopupRow
